import { inject, Injectable } from '@angular/core';

import {
  ANALYTICS_VIEW_CONTEXT,
  AnalyticsClient,
  AnalyticsConfig,
  AnalyticsProps,
  AnalyticsAPIMethods,
  AnalyticsClientOptions,
  throwNoContextError,
  throwReservedContextPropertyError,
} from '@core/analytics-types';

@Injectable()
export abstract class AnalyticsBase implements AnalyticsAPIMethods {
  public abstract readonly namespace: string;
  public abstract readonly requireViewContext: boolean;
  public defaultTrackOptions: AnalyticsClientOptions = {
    integrations: {
      All: true,
      Intercom: false,
    },
  };

  private analyticsClient = inject(AnalyticsClient);

  private config = inject(AnalyticsConfig);
  private viewContext = inject(ANALYTICS_VIEW_CONTEXT, { optional: true });

  public track(
    evtName: string,
    evtProps?: AnalyticsProps,
    options: AnalyticsClientOptions = this.defaultTrackOptions,
  ) {
    const props = this.requireViewContext ? this.withContext(evtProps) : evtProps;

    if (this.requireViewContext && props === undefined) {
      return;
    }

    if (this.namespace !== '') {
      this.analyticsClient.track(`[${this.namespace}] ${evtName}`, props, options);
    } else {
      this.analyticsClient.track(evtName, props, options);
    }
  }

  public alias(id: number): void {
    this.analyticsClient.alias(id);
  }

  public identify(
    userId?: number,
    traits?: Record<string, string | number>,
    options?: AnalyticsClientOptions,
  ): void {
    this.analyticsClient.identify(userId, traits, options);
  }

  public page(
    category: string,
    name: string,
    properties?: any,
    options?: AnalyticsClientOptions,
  ): void {
    this.analyticsClient.page(category, name, properties, options);
  }

  public reset() {
    this.analyticsClient.reset();
  }

  private withContext(evtProps: AnalyticsProps = {}) {
    if (!Array.isArray(this.viewContext) || this.viewContext.length === 0) {
      if (this.config?.isDebug) {
        throwNoContextError();
      } else {
        return;
      }
    }

    if (evtProps && evtProps.viewContext) {
      if (this.config?.isDebug) {
        throwReservedContextPropertyError();
      } else {
        return;
      }
    }

    return {
      ...evtProps,
      viewContext: this.viewContext,
    };
  }
}
